import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { ArrowRight, FileEdit } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { CaseDetail } from '../types'
import type { AlertSummary } from '@/features/alerts/types'

/**
 * CaseDetectionSummary — what fired this case.
 *
 * Groups linked alerts by their triggering scenario so duplicate
 * alerts on the same scenario collapse into a single row. Each row
 * is a deep-link to the alert's detail page, where the WhySection
 * surfaces the full rule list + per-rule evidence dict. This card
 * answers the analyst's first question on a fresh case ("what
 * detection logic decided this was suspicious?") without
 * duplicating the alert page's evidence grid here.
 */
export function CaseDetectionSummary({ c }: { c: CaseDetail }) {
  if (c.linked_alerts.length === 0) return null
  const groups = groupByScenario(c.linked_alerts)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <FileEdit size={14} className="text-primary" aria-hidden />
          Detection summary
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <p className="mb-3 text-xs text-muted-fg">
          {groups.length === 1
            ? '1 scenario fired across this case.'
            : `${groups.length} scenarios fired across this case.`}{' '}
          Open any row to see the rules that triggered the alert and the
          supporting evidence the engine captured.
        </p>
        <ul className="space-y-2">
          {groups.map((g) => (
            <li
              key={g.scenario_code}
              className="rounded border border-border bg-muted/15 p-2.5"
            >
              <div className="flex items-baseline gap-2">
                <Badge variant="info" className="font-mono">
                  {g.scenario_code}
                </Badge>
                <span className="text-sm font-medium">{g.scenario_name}</span>
                <span className="ml-auto text-[0.625rem] text-muted-fg">
                  {g.alerts.length === 1
                    ? '1 alert'
                    : `${g.alerts.length} alerts`}
                </span>
              </div>
              <ul className="mt-2 space-y-1">
                {g.alerts.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/alerts/${a.id}`}
                      className={cn(
                        'flex items-center justify-between gap-2 rounded border border-border bg-card px-2 py-1 text-xs',
                        'hover:border-primary/50 hover:bg-primary/[0.04]',
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-[0.625rem] text-muted-fg">
                          {a.alert_number}
                        </span>
                        <PriorityPill priority={a.priority} />
                        <span className="text-fg">{a.customer.display_name}</span>
                      </span>
                      <span className="flex items-center gap-1 text-primary">
                        Rules &amp; evidence <ArrowRight size={10} aria-hidden />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

interface ScenarioGroup {
  scenario_code: string
  scenario_name: string
  alerts: AlertSummary[]
}

function groupByScenario(alerts: ReadonlyArray<AlertSummary>): ScenarioGroup[] {
  const map = new Map<string, ScenarioGroup>()
  for (const a of alerts) {
    const key = a.scenario_code
    const existing = map.get(key)
    if (existing) {
      existing.alerts.push(a)
    } else {
      map.set(key, {
        scenario_code: a.scenario_code,
        scenario_name: a.scenario_name,
        alerts: [a],
      })
    }
  }
  // Newest scenarios first, then alphabetical for stability across
  // re-renders of the same group.
  return Array.from(map.values()).sort((x, y) => {
    if (x.alerts.length !== y.alerts.length) return y.alerts.length - x.alerts.length
    return x.scenario_code.localeCompare(y.scenario_code)
  })
}

function PriorityPill({ priority }: { priority: AlertSummary['priority'] }) {
  const variant: 'failed' | 'pending' | 'info' | 'default' =
    priority === 'critical'
      ? 'failed'
      : priority === 'high'
        ? 'pending'
        : priority === 'medium'
          ? 'info'
          : 'default'
  return (
    <Badge variant={variant} className="uppercase">
      {priority}
    </Badge>
  )
}
